/**
 * Help docs — renders published docs_pages. A left index of published articles +
 * the selected article rendered via the shared <MarkdownView> (the same renderer
 * the arbi-dev CMS previews with — the RBP:1387 round-trip).
 *
 * Day 100b.1 chrome: the body's `# {title}` is the canonical title (no chrome h1,
 * so CMS-preview body-only === arbi-docs), a "Last updated" date (docs_pages
 * .updated_at — item-7, date only, no version-history UI / revisions table), and
 * an "On this page" anchor-TOC built from buildDocsToc() in the PAGE chrome
 * (OUTSIDE MarkdownView, so the CMS preview gets no TOC) linking rehype-slug's
 * heading ids via in-page #id hashes. The per-route slug registry, contextual
 * "?", and search are Day 100b.2.
 *
 * An empty state (TONE_STANDARDS) shows when nothing is published — NOT a "coming
 * soon" placeholder (per-slug coming-soon is 100b.2). RLS lets every authenticated
 * role read published rows.
 */
import { useState, useEffect, type JSX } from 'react';
import { FileText } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import MarkdownView from '../components/ui/MarkdownView';
import { buildDocsToc } from '../../../../src/lib/docsToc';
import type { Database } from '../../../../src/lib/database.types';

type DocsPage = Database['public']['Tables']['docs_pages']['Row'];

// Short "last updated" date for the article chrome (docs_pages.updated_at).
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function HelpDocs(): JSX.Element {
  useDocumentTitle('Help docs');
  const [rows, setRows] = useState<DocsPage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      const { data } = await supabase
        .from('docs_pages')
        .select('*')
        // Published-only even for admins — the docs site shows the published view;
        // drafts are the arbi-dev CMS preview's concern (RLS lets admin read drafts,
        // so this explicit filter is what keeps the site role-independent).
        .eq('is_published', true)
        .order('title', { ascending: true });
      if (!active) return;
      const list = data ?? [];
      setRows(list);
      setSelectedId((prev) => prev ?? list[0]?.id ?? null);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const selected = rows.find((r) => r.id === selectedId) ?? null;
  // "On this page" TOC: the body's sub-headings (depth ≥ 2 — the page-title h1 is
  // shown at the top of the article, not repeated in the rail). buildDocsToc's ids
  // match rehype-slug's so the in-page #id links resolve to the rendered headings.
  const sections = selected ? buildDocsToc(selected.body).filter((e) => e.depth >= 2) : [];

  if (loading) {
    return <div className="h-full overflow-auto p-6 text-sm text-ink-muted">Loading…</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="h-full overflow-auto">
        <div className="max-w-3xl mx-auto p-8">
          <div className="arbi-card p-10 text-center">
            <FileText size={22} className="mx-auto text-ink-muted mb-3" />
            <h1 className="text-base font-semibold text-ink-bright">No help articles yet</h1>
            <p className="text-sm text-ink-secondary mt-1">
              Published help articles will appear here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex min-h-0">
      {/* Article index */}
      <div className="w-60 flex-shrink-0 border-r border-border overflow-auto p-3 space-y-0.5">
        {rows.map((doc) => (
          <button
            key={doc.id}
            type="button"
            onClick={() => setSelectedId(doc.id)}
            className={clsx(
              'w-full text-left px-3 py-2 rounded-md text-sm transition-colors truncate',
              doc.id === selectedId
                ? 'bg-accent/15 text-accent font-medium'
                : 'text-ink-secondary hover:text-ink-primary hover:bg-surface-2'
            )}
          >
            {doc.title}
          </button>
        ))}
      </div>

      {/* Rendered article — no chrome h1; the body's `# {title}` is the title */}
      <div className="flex-1 min-w-0 overflow-auto">
        <div className="max-w-3xl mx-auto p-6">
          {selected ? (
            <>
              <p className="text-2xs text-ink-muted mb-4">
                Last updated {fmtDate(selected.updated_at)}
              </p>
              <MarkdownView
                markdown={selected.body}
                onInternalLink={(s) => {
                  const row = rows.find((r) => r.slug === s);
                  if (row) setSelectedId(row.id);
                }}
              />
            </>
          ) : (
            <p className="text-sm text-ink-muted">Select an article.</p>
          )}
        </div>
      </div>

      {/* "On this page" anchor TOC — page chrome, OUTSIDE MarkdownView (the CMS
          preview gets no TOC); in-page #id links to rehype-slug's heading ids.
          Shown only when the article has ≥ 2 sub-headings — short docs get no rail. */}
      {sections.length >= 2 && (
        <nav
          aria-label="On this page"
          className="w-56 flex-shrink-0 border-l border-border overflow-auto p-4"
        >
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-2">On this page</p>
          <ul className="space-y-1">
            {sections.map((entry) => (
              <li key={entry.id} style={{ paddingLeft: `${(entry.depth - 2) * 12}px` }}>
                <a
                  href={`#${entry.id}`}
                  className="block text-xs text-ink-secondary hover:text-accent truncate"
                >
                  {entry.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
